// src/lib/currency.ts
// -----------------------------------------------------------------------------
// Multi-currency pricing. Base prices are in EGP; `rate` is the target-currency
// value of 1 EGP. A tiny store (localStorage + custom event) syncs the choice
// across components without a provider. Initial value is deterministic ("EGP")
// so server and first client render match (no hydration mismatch).
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import type { Bilingual } from "@/types/home";

export interface Currency {
  code: string;
  label: Bilingual;
  symbol: Bilingual;
  rate: number;
  prefix?: boolean; // symbol before the number
}

export const CURRENCIES: Record<string, Currency> = {
  EGP: { code: "EGP", label: { ar: "جنيه مصري", en: "Egyptian Pound" }, symbol: { ar: "ج.م", en: "EGP" }, rate: 1 },
  USD: { code: "USD", label: { ar: "دولار أمريكي", en: "US Dollar" }, symbol: { ar: "$", en: "$" }, rate: 0.021, prefix: true },
  EUR: { code: "EUR", label: { ar: "يورو", en: "Euro" }, symbol: { ar: "€", en: "€" }, rate: 0.019, prefix: true },
  AED: { code: "AED", label: { ar: "درهم إماراتي", en: "UAE Dirham" }, symbol: { ar: "د.إ", en: "AED" }, rate: 0.077 },
  SAR: { code: "SAR", label: { ar: "ريال سعودي", en: "Saudi Riyal" }, symbol: { ar: "ر.س", en: "SAR" }, rate: 0.079 },
};

export type CurrencyCode = keyof typeof CURRENCIES;
const KEY = "tahawer-currency";
const EVENT = "tahawer-currency-change";

export function formatPrice(egp: number, code: string, locale: string): string {
  const c = CURRENCIES[code] ?? CURRENCIES.EGP;
  const value = Math.round(egp * c.rate);
  const num = new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US").format(value);
  const sym = locale === "ar" ? c.symbol.ar : c.symbol.en;
  return c.prefix ? `${sym}${num}` : `${num} ${sym}`;
}

/** Reactive currency selection shared across the page. */
export function useCurrency() {
  const [code, setCode] = useState<string>("EGP");

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (stored && CURRENCIES[stored]) setCode(stored);
    const onChange = (e: Event) => setCode((e as CustomEvent<string>).detail);
    const onStorage = () => setCode(localStorage.getItem(KEY) ?? "EGP");
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const change = (next: string) => {
    localStorage.setItem(KEY, next);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
    setCode(next);
  };

  return { code, change };
}
